import { useTranslate } from '@growtherapy/i18n';
import { Button } from '@growtherapy/sprout-ui';

import { MeasureResponseCompletionCampaign } from '@grow-therapy-team/seedling-components';

import { PageComponentProps } from '../../components';
import { useSubmitMeasureResponseCallback } from '../../hooks/measures/useSubmitMeasureResponseCallback';
import { MeasureSchemaAndAnswers } from './types';

export type MeasureActionsProps = Pick<PageComponentProps<MeasureSchemaAndAnswers>, 'isFirstPage' | 'isLastPage'> & {
  isLoading?: boolean;
  onNextPage: () => void;
  onPrevPage: () => void;
};

export function MeasuresActions({ isFirstPage, isLastPage, isLoading, onNextPage, onPrevPage }: MeasureActionsProps) {
  const t = useTranslate();
  return (
    <>
      {!isFirstPage && (
        <Button onClick={onPrevPage} use='secondary' disabled={isLoading}>
          {t('telehealth-core:core.previous')}
        </Button>
      )}
      <Button onClick={onNextPage} loading={isLoading} disabled={isLoading}>
        {isLastPage ? t('telehealth-core:core.complete') : t('telehealth-core:core.continue')}
      </Button>
    </>
  );
}

type MeasuresActionsWrapperProps = PageComponentProps<MeasureSchemaAndAnswers> & {
  clientUserShortId?: string;
  providerShortId?: string;
} & {
  completionCampaign: MeasureResponseCompletionCampaign;
  onSubmitMeasureSuccess?: () => void;
};

export function MeasuresActionsWrapper({
  clientUserShortId,
  completionCampaign,
  page,
  providerShortId,
  isFirstPage,
  isLastPage,
  prevPage,
  nextPage,
  setPage,
  scrollToTop,
  onSubmitMeasureSuccess,
}: MeasuresActionsWrapperProps) {
  const [submitMeasure, { isLoading }] = useSubmitMeasureResponseCallback();

  const goToNextMeasure = () => {
    if (isLastPage) {
      return;
    }
    setPage((prev) => ({ ...prev, hasSubmitted: true }));
    nextPage();
    scrollToTop();
  };

  const handleMeasureSubmission = async () => {
    const { status } = await submitMeasure({
      page,
      clientUserShortId,
      providerShortId,
      completionCampaign,
    });

    if (status !== 'success') {
      return;
    }

    onSubmitMeasureSuccess?.();
    goToNextMeasure();
  };

  const handlePreviousPage = () => {
    prevPage();
    scrollToTop();
  };

  return (
    <MeasuresActions
      isFirstPage={isFirstPage}
      isLastPage={isLastPage}
      isLoading={isLoading}
      onNextPage={handleMeasureSubmission}
      onPrevPage={handlePreviousPage}
    />
  );
}
